import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { invoiceService } from '../services/invoiceService';
import { companyService } from '../services/companyService';
import { Invoice } from '../types/invoice';
import { Company } from '../types/company';
import { Button, showToast } from '../components/UI';
import { PrinterIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const InvoiceView: React.FC = () => {
  // Add A5 print styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        @page {
          size: A5 portrait;
          margin: 5mm;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const loadData = async () => {
      try {
        setLoading(true);
        const [invoiceData, companyData] = await Promise.all([
          invoiceService.getInvoiceById(parseInt(id)),
          companyService.getActiveCompany().catch(() => null)
        ]);
        if (cancelled) return;
        setInvoice(invoiceData);
        setCompany(companyData);
      } catch (error: any) {
        if (cancelled) return;
        console.error('Error loading data:', error);
        showToast.error('Failed to load invoice');
        navigate('/invoices');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => { cancelled = true; };
  }, [id, navigate]);

  const handlePrint = () => {
    // Set A5 paper size for printing
    const printCSS = `
      @page {
        size: A5 portrait;
        margin: 5mm;
      }
      @media print {
        body {
          width: 148mm;
          height: 210mm;
        }
      }
    `;

    const styleElement = document.createElement('style');
    styleElement.innerHTML = printCSS;
    document.head.appendChild(styleElement);

    // Show reminder to user
    showToast.info('Please select A5 paper size in the print dialog');

    window.print();

    // Clean up
    setTimeout(() => {
      document.head.removeChild(styleElement);
    }, 100);
  };

  const handleBack = () => {
    navigate('/invoices');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Invoice not found</p>
          <Button onClick={handleBack}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden bg-white shadow-sm border-b px-6 py-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <Button
            onClick={handleBack}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span>Back to Invoices</span>
          </Button>
          <Button
            onClick={handlePrint}
            className="flex items-center space-x-2"
          >
            <PrinterIcon className="w-4 h-4" />
            <span>Print Estimate</span>
          </Button>
        </div>
      </div>

      {/* Printable Content */}
      <div className="max-w-4xl mx-auto bg-white print:shadow-none print:max-w-none print:mx-0">
        <div className="p-3 sm:p-8 print:p-4">
          {/* Header */}
          <div className="mb-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 gap-2">
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 print:text-black mb-1">ESTIMATE</h1>
                <div className="text-xs text-gray-600 print:text-black space-y-0">
                  <p><strong>Estimate No:</strong> {invoice.invoice_no}</p>
                  <p><strong>Date:</strong> {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString('en-GB') : '-'}</p>
                  <p><strong>Log Code:</strong> {invoice.logSheet?.log_code || '-'}</p>
                </div>
              </div>
              <div className="sm:text-right min-w-0">
                <h2 className="text-base sm:text-lg font-bold text-blue-600 print:text-black mb-1 break-words">
                  {company?.name || 'Your Company Name'}
                </h2>
                <div className="text-xs text-gray-600 print:text-black space-y-0">
                  {company?.address && <p className="break-words">{company.address}</p>}
                  {(company?.city || company?.state || company?.pincode) && (
                    <p className="break-words">
                      {[company?.city, company?.state].filter(Boolean).join(', ')}
                      {company?.pincode && ` - ${company.pincode}`}
                    </p>
                  )}
                  {company?.phone && <p>Phone: {company.phone}</p>}
                  {company?.email && <p className="break-all">Email: {company.email}</p>}
                  {company?.gst_no && <p>GST: {company.gst_no}</p>}
                  {company?.website && <p className="break-all">Website: {company.website}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="mb-3">
            <h3 className="text-sm font-semibold mb-2 text-gray-900 print:text-black">Customer Details</h3>
            <div className="bg-gray-50 p-2 rounded">
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-900 print:text-black">
                <div className="space-y-0.5 min-w-0 overflow-hidden">
                  <p className="break-words"><strong>Company:</strong> {invoice.logSheet?.enquiry?.company_name || '-'}</p>
                  <p className="break-words"><strong>Contact:</strong> {invoice.logSheet?.enquiry?.contact_person || '-'}</p>
                  <p className="break-words"><strong>Phone:</strong> {invoice.logSheet?.enquiry?.contact_no || '-'}</p>
                  {(invoice.logSheet?.enquiry?.street || invoice.logSheet?.enquiry?.town || invoice.logSheet?.enquiry?.district) && (
                    <p className="break-words line-clamp-2"><strong>Addr:</strong> {[invoice.logSheet?.enquiry?.street, invoice.logSheet?.enquiry?.town, invoice.logSheet?.enquiry?.district].filter(Boolean).join(', ')}</p>
                  )}
                </div>
                <div className="space-y-0.5 min-w-0 overflow-hidden">
                  <p className="break-words"><strong>Enq No:</strong> {invoice.logSheet?.enquiry?.enquiry_no || '-'}</p>
                  <p className="break-words"><strong>Svc Date:</strong> {invoice.logSheet?.enquiry?.service_date ? new Date(invoice.logSheet.enquiry.service_date).toLocaleDateString('en-GB') : '-'}</p>
                  <p className="break-words"><strong>Bore:</strong> {invoice.logSheet?.enquiry?.boreSize?.size || '-'}</p>
                  <p className="break-words"><strong>Rig:</strong> {invoice.logSheet?.rig_no || '-'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Estimate Details */}
          <div className="mb-3">
            <h3 className="text-sm font-semibold mb-2 text-gray-900 print:text-black">Estimate Details</h3>
            <table className="w-full table-fixed border-collapse border border-gray-300">
              <colgroup>
                <col style={{ width: '7%' }} />
                <col style={{ width: '48%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '18%' }} />
              </colgroup>
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-1 py-1 text-left text-xs font-semibold text-black">#</th>
                  <th className="border border-gray-300 px-2 py-1 text-left text-xs font-semibold text-black">Particulars</th>
                  <th className="border border-gray-300 px-1 py-1 text-center text-xs font-semibold text-black">Qty</th>
                  <th className="border border-gray-300 px-1 py-1 text-right text-xs font-semibold text-black">Rate</th>
                  <th className="border border-gray-300 px-1 py-1 text-right text-xs font-semibold text-black">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.invoiceDetails?.map((detail, index) => (
                  <tr key={detail.id}>
                    <td className="border border-gray-300 px-1 py-1 text-xs text-center text-black align-top">{index + 1}</td>
                    <td className="border border-gray-300 px-2 py-1 text-xs text-black align-top">
                      <div className="line-clamp-3 break-words" title={detail.particulars ?? undefined}>
                        {detail.particulars}
                      </div>
                    </td>
                    <td className="border border-gray-300 px-1 py-1 text-xs text-center text-black align-top">{detail.qty || 0}</td>
                    <td className="border border-gray-300 px-1 py-1 text-xs text-right text-black align-top break-words">₹{(detail.rate || 0).toLocaleString('en-IN')}</td>
                    <td className="border border-gray-300 px-1 py-1 text-xs text-right text-black align-top break-words">₹{(detail.amount || 0).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Total Summary */}
          <div className="mb-3">
            <div className="flex justify-end">
              <div className="w-full sm:w-1/2 max-w-md">
                <div className="bg-gray-50 p-2 rounded">
                  <div className="space-y-1 text-xs text-black">
                    {invoice.less_amount && invoice.less_amount > 0 ? (
                      <>
                        <div className="flex justify-between gap-2">
                          <span className="font-medium whitespace-nowrap">Subtotal:</span>
                          <span className="text-right">₹{((invoice.net_amount || 0) + (invoice.less_amount || 0)).toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between gap-2 text-red-600">
                          <span className="font-medium whitespace-nowrap">Less:</span>
                          <span className="text-right">₹{(invoice.less_amount || 0).toLocaleString('en-IN')}</span>
                        </div>
                        <hr className="border-gray-300" />
                        <div className="flex justify-between gap-2">
                          <span className="text-sm font-bold whitespace-nowrap">Net Amount:</span>
                          <span className="text-sm font-bold text-right">₹{(invoice.net_amount || 0).toLocaleString('en-IN')}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between gap-2">
                        <span className="text-sm font-bold whitespace-nowrap">Net Amount:</span>
                        <span className="text-sm font-bold text-right">₹{(invoice.net_amount || 0).toLocaleString('en-IN')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t pt-3">
            <div className="flex justify-between items-end gap-4">
              <div className="text-xs text-black space-y-0 min-w-0">
                <p className="break-words"><strong>Created by:</strong> {invoice.createdByUser?.name || '-'}</p>
                <p><strong>Created on:</strong> {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString('en-GB') : '-'}</p>
              </div>
              <div className="text-center flex-shrink-0">
                <div className="border-t border-gray-400 w-32 sm:w-40 mt-8 pt-1">
                  <p className="text-xs font-medium text-black">Authorized Signature</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceView;